import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Card,
  CardContent,
  Autocomplete,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ReferralsTab from './ReferralsTab';
import PatientSummaryTab from './PatientSummaryTab';
import CareTeamTab from './CareTeamTab';
import CarePlanTab from './CarePlanTab';
import DirectMessagingTab from './DirectMessagingTab';
import ReferralAdmission from './ReferralAdmission';

import { getAllPatients } from '../../services/careCoordinationService';
import { getAllReferrals } from '../../services/careCoordinationService';
import { useUserData } from '../../hooks/useUserData';
import { getAllPatients as getPatientsFromService } from '../../services/patientService';
import { store } from '../../redux/store';
import { safeFormatDate } from '../../utils/dateUtils';

// Styled Components - Modern Theme
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(25, 118, 210, 0.3)',
}));

const ModernCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  marginBottom: theme.spacing(3),
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: '3px',
    borderRadius: '3px',
  },
  '& .MuiTab-root': {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 600,
    '&.Mui-selected': {
      color: theme.palette.primary.main,
    },
  },
}));



function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`carecoordination-tabpanel-${index}`}
      aria-labelledby={`carecoordination-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function CareCoordination() {
  const { defaultFacility } = useUserData();
  
  const [tab, setTab] = useState(0); // 0 = Referral Admission (now first tab)
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState('');

  useEffect(() => {
    async function fetchReferralPatients() {
      if (!defaultFacility) {
        console.warn('No default facility set for user');
        setPatients([]);
        return;
      }

      console.log('CareCoordination: Starting to fetch patients for facility:', defaultFacility.id);
      console.log('CareCoordination: Default facility details:', defaultFacility);

      try {
        // Get all patients first (this is what we want to show in the dropdown)
        console.log('CareCoordination: Calling getAllPatients...');
        const allPatients = await getAllPatients(defaultFacility.id);
        console.log('CareCoordination: getAllPatients response:', allPatients);
        
        const patientsArray = Array.isArray(allPatients) ? allPatients : [];
        
        // If no patients from careCoordinationService, try patientService as fallback
        if (patientsArray.length === 0) {
          console.log('CareCoordination: No patients from careCoordinationService, trying patientService fallback...');
          try {
            const fallbackPatients = await getPatientsFromService({ facilityId: defaultFacility.id });
            console.log('CareCoordination: Fallback patients response:', fallbackPatients);
            if (Array.isArray(fallbackPatients) && fallbackPatients.length > 0) {
              console.log('CareCoordination: Using fallback patients:', fallbackPatients.length);
              setPatients(fallbackPatients);
            } else {
              console.log('CareCoordination: No patients from fallback either, trying to get patients from all facilities...');
              // Last resort: try to get patients from all facilities the user has access to
              try {
                const state = store.getState();
                const userFacilities = state.facilities.userFacilities;
                if (userFacilities && userFacilities.length > 0) {
                  console.log('CareCoordination: Trying to get patients from user facilities:', userFacilities.length);
                  let allFacilityPatients = [];
                  for (const facility of userFacilities.slice(0, 3)) { // Limit to first 3 facilities
                    try {
                      const facilityPatients = await getPatientsFromService({ facilityId: facility.id });
                      if (Array.isArray(facilityPatients)) {
                        allFacilityPatients = allFacilityPatients.concat(facilityPatients);
                      }
                    } catch (facilityError) {
                      console.warn(`Could not fetch patients from facility ${facility.id}:`, facilityError);
                    }
                  }
                  if (allFacilityPatients.length > 0) {
                    console.log('CareCoordination: Found patients from multiple facilities:', allFacilityPatients.length);
                    setPatients(allFacilityPatients);
                  } else {
                    console.log('CareCoordination: No patients found in any facility');
                    setPatients([]);
                  }
                } else {
                  console.log('CareCoordination: No user facilities available');
                  setPatients([]);
                }
              } catch (multiFacilityError) {
                console.warn('CareCoordination: Multi-facility fallback failed:', multiFacilityError);
                setPatients([]);
              }
            }
          } catch (fallbackError) {
            console.warn('CareCoordination: Fallback patient service also failed:', fallbackError);
            setPatients([]);
          }
        } else {
          console.log('CareCoordination: Fetched all patients:', {
            facilityId: defaultFacility.id,
            totalPatients: patientsArray.length,
            patientsData: patientsArray.slice(0, 3) // Log first 3 patients for debugging
          });
          
          // Set all patients in the dropdown
          setPatients(patientsArray);
        }
        
        // Also fetch referrals for reference (but don't filter patients by them)
        try {
          console.log('CareCoordination: Calling getAllReferrals...');
          const referrals = await getAllReferrals(defaultFacility.id);
          if (Array.isArray(referrals)) {
            console.log('CareCoordination: Fetched referrals:', {
              totalReferrals: referrals.length,
              referralsWithPatients: referrals.filter(r => r.patientId).length
            });
          }
        } catch (referralError) {
          console.warn('Could not fetch referrals, but continuing with patient list:', referralError);
        }
        
      } catch (error) {
        console.error('Error fetching patients:', error);
        console.error('Error details:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        setPatients([]);
      }
    }
    fetchReferralPatients();
  }, [defaultFacility]);

  const handleChange = (event, newValue) => {
    setTab(newValue);
  };

  // Show error state if no default facility is set
  if (!defaultFacility) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          No Default Facility Set
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your account does not have a default facility assigned. Please contact your administrator to set up facility access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', minHeight: '100vh' }}>
      <HeaderCard>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <HealthAndSafetyIcon sx={{ fontSize: 40, opacity: 0.9 }} />
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Care Coordination
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Manage patient care coordination, referrals, and care plans
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </HeaderCard>

      <ModernCard>
        <CardContent>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Autocomplete
              sx={{ minWidth: 220 }}
              size="small"
              options={[{ id: '', firstName: 'All', lastName: 'Patients', dateOfBirth: null }, ...patients]}
              getOptionLabel={(option) => {
                if (!option) return '';
                if (option.id === '') return 'All Patients';
                // Handle both new firstName/lastName structure and legacy name field
                const displayName = option.firstName && option.lastName 
                  ? `${option.firstName} ${option.lastName}` 
                  : option.name || 'Unknown Patient';
                const dateOfBirth = safeFormatDate(option.dateOfBirth, 'MMM dd, yyyy', 'N/A');
                return `${displayName} (${dateOfBirth})`;
              }}
              value={selectedPatientId === '' ? { id: '', firstName: 'All', lastName: 'Patients', dateOfBirth: null } : patients.find(p => (p.patientId || p.id) === selectedPatientId) || null}
              onChange={(event, newValue) => {
                const patientId = newValue ? (newValue.patientId || newValue.id) : '';
                setSelectedPatientId(patientId);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Patient"
                  placeholder="Search patients..."
                />
              )}
              isOptionEqualToValue={(option, value) => 
                (option.patientId || option.id) === (value.patientId || value.id)
              }
              noOptionsText="No patients found"
              loading={patients.length === 0}
              filterOptions={(options, { inputValue }) => {
                if (!inputValue) return options;
                const searchTerm = inputValue.toLowerCase();
                return options.filter(option => {
                  if (option.id === '') return true; // Always show "All Patients"
                  const displayName = option.firstName && option.lastName 
                    ? `${option.firstName} ${option.lastName}` 
                    : option.name || '';
                  return displayName.toLowerCase().includes(searchTerm);
                });
              }}
            />
            <Typography variant="body2" color="text.secondary">
              Filter all tabs by patient
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {patients.length} patients loaded
            </Typography>
          </Box>
          
          <StyledTabs
            value={tab}
            onChange={handleChange}
            aria-label="Care Coordination Tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            <Tab label="Referral Admission" id="carecoordination-tab-0" aria-controls="carecoordination-tabpanel-0" />
            <Tab label="Referrals" id="carecoordination-tab-1" aria-controls="carecoordination-tabpanel-1" />
            <Tab label="Patient Summary" id="carecoordination-tab-2" aria-controls="carecoordination-tabpanel-2" />
            <Tab label="Care Team" id="carecoordination-tab-3" aria-controls="carecoordination-tabpanel-3" />
            <Tab label="Care Plan" id="carecoordination-tab-4" aria-controls="carecoordination-tabpanel-4" />
            <Tab label="Direct Messaging" id="carecoordination-tab-5" aria-controls="carecoordination-tabpanel-5" />
          </StyledTabs>
          
          <TabPanel value={tab} index={0}>
            <ReferralAdmission patientId={selectedPatientId} patients={patients} facilityId={defaultFacility?.id} />
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <ReferralsTab patientId={selectedPatientId} patients={patients} facilityId={defaultFacility?.id} />
          </TabPanel>
          <TabPanel value={tab} index={2}>
            <PatientSummaryTab patientId={selectedPatientId} patients={patients} />
          </TabPanel>
          <TabPanel value={tab} index={3}>
            <CareTeamTab patientId={selectedPatientId} patients={patients} />
          </TabPanel>
          <TabPanel value={tab} index={4}>
            <CarePlanTab patientId={selectedPatientId} patients={patients} />
          </TabPanel>
          <TabPanel value={tab} index={5}>
            <DirectMessagingTab patientId={selectedPatientId} patients={patients} />
          </TabPanel>
          
        </CardContent>
      </ModernCard>
    </Box>
  );
} 